import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { avatarProxyPath } from '../../lib/roster';

export interface CharacterTabItem {
  /** A real character id, or `'overview'` for the trailing whole-account tab. */
  id: number | 'overview';
  label: string;
  /** Class/level microtext under the name — omitted for the Overview tab. */
  microtext?: string;
  /** REV4: server-fetched GMS rankings avatar — omitted (never shown) for the Overview tab. */
  avatarUrl?: string | null;
  /** The character's account id — required alongside a non-null `avatarUrl` to build the REV12 proxy path. */
  accountId?: number;
}

/**
 * REV12: the tab strip's avatar circle, split out so each tab can independently fall back to the
 * initial-letter block on an `<img>` load failure (a 404 from an expired upstream URL) without a
 * hooks-in-a-loop violation in the `tabs.map` below.
 */
function TabAvatar({ src, label }: { src: string | null; label: string }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  return (
    <span className="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-bg-elevated-hover text-[0.6rem] font-semibold text-text-faint">
      {src && !failed ? (
        <img src={src} alt="" loading="lazy" className="size-full object-cover" onError={() => setFailed(true)} />
      ) : (
        <span aria-hidden="true">{label.trim().slice(0, 1).toUpperCase() || '?'}</span>
      )}
    </span>
  );
}

interface CharacterTabStripProps {
  tabs: CharacterTabItem[];
  selectedId: number | 'overview';
  onSelect: (id: number | 'overview') => void;
}

/**
 * The "Character tab strip" recipe (docs/DESIGN.md component recipes) — a flat, horizontally
 * scrollable, PowerShell-terminal-style tab row: inactive tabs sit flush on `--bg`, the active tab
 * lifts to `--bg-elevated` with a 2px `--accent` top border. One tab per character (name + small
 * class/level microtext) in roster order, plus a trailing "Overview" tab for the whole-account
 * matrix. Real `role="tablist"` semantics; Left/Right/Home/End move focus and selection together
 * (a tab strip, unlike a toolbar, activates on arrow navigation rather than requiring a second
 * Enter/Space per the WAI-ARIA "automatic activation" tabs pattern).
 */
export function CharacterTabStrip({ tabs, selectedId, onSelect }: CharacterTabStripProps) {
  const tabRefs = useRef<Map<number | 'overview', HTMLButtonElement>>(new Map());

  const focusAndSelect = (id: number | 'overview') => {
    onSelect(id);
    tabRefs.current.get(id)?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (tabs.length === 0) {
      return;
    }
    let target: CharacterTabItem | undefined;
    if (event.key === 'ArrowRight') {
      target = tabs[(index + 1) % tabs.length];
    } else if (event.key === 'ArrowLeft') {
      target = tabs[(index - 1 + tabs.length) % tabs.length];
    } else if (event.key === 'Home') {
      target = tabs[0];
    } else if (event.key === 'End') {
      target = tabs[tabs.length - 1];
    }
    if (target) {
      event.preventDefault();
      focusAndSelect(target.id);
    }
  };

  return (
    <div
      role="tablist"
      aria-label="Character"
      className="flex gap-1 overflow-x-auto border-b border-border"
      style={{
        // Edge fade (docs/DESIGN.md tab-strip recipe) so a scrollable overflow reads as
        // continuing off-screen rather than abruptly clipping the last visible tab.
        maskImage: 'linear-gradient(to right, transparent 0, black 12px, black calc(100% - 12px), transparent 100%)',
        WebkitMaskImage:
          'linear-gradient(to right, transparent 0, black 12px, black calc(100% - 12px), transparent 100%)',
      }}
    >
      {tabs.map((tab, index) => {
        const isSelected = tab.id === selectedId;
        return (
          <button
            key={tab.id}
            ref={(el) => {
              if (el) {
                tabRefs.current.set(tab.id, el);
              } else {
                tabRefs.current.delete(tab.id);
              }
            }}
            type="button"
            role="tab"
            id={`boss-tab-${tab.id}`}
            aria-selected={isSelected}
            aria-controls={`boss-tabpanel-${tab.id}`}
            tabIndex={isSelected ? 0 : -1}
            onClick={() => onSelect(tab.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={[
              'flex min-h-tap shrink-0 items-center gap-2 whitespace-nowrap border-t-2 px-4 py-1.5 text-left transition-colors',
              isSelected
                ? 'border-t-accent bg-bg-elevated text-text'
                : 'border-t-transparent bg-bg text-text-muted hover:text-text',
            ].join(' ')}
          >
            {tab.avatarUrl !== undefined && (
              <TabAvatar
                src={
                  tab.avatarUrl != null && typeof tab.id === 'number' && tab.accountId != null
                    ? avatarProxyPath(tab.accountId, tab.id)
                    : null
                }
                label={tab.label}
              />
            )}
            <span className="flex flex-col items-start gap-0.5">
              <span className="text-sm font-semibold">{tab.label}</span>
              {tab.microtext && <span className="text-[0.65rem] text-text-faint">{tab.microtext}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}
